import React from 'react';
import { useIntl } from 'react-intl';

import { useIntlCurrency } from 'src/util/i18n/currency';
import { buildDescription, buildTimeOfUseableElementConditions } from 'src/util/i18n/timeOfUse';

import { valuePerUnitWithStrong } from '../energyPrice';

/**
 * Custom hook for formatting time-of-usable entities with internationalized pricing,
 * descriptions, and conditions.
 *
 * This hook processes time-of-usable entities (such as Clause or VolumeCharge objects)
 * to provide formatted display values including currency-converted pricing, human-readable
 * time-of-use descriptions, and conditional formatting for timezone and holiday settings.
 * It combines multiple i18n utilities to create a complete display representation.
 * @param {object} timeOfUsable - The time-of-usable entity containing pricing and time-of-use
 *   configuration. This typically corresponds to GraphQL types like Clause or VolumeCharge
 * @param {string} timeOfUsable.id - Unique identifier for the time-of-usable entity
 * @param {number} [timeOfUsable.price] - Price value in internal format (when valueField is
 * 'price')
 * @param {number} [timeOfUsable.rate] - Rate value in internal format (when valueField is 'rate')
 * @param {string} [timeOfUsable.timezone] - Timezone for time calculations
 * @param {boolean} [timeOfUsable.ignoreDaylightSavings] - Whether to ignore daylight savings time
 * @param {boolean} [timeOfUsable.ignorePublicHolidays] - Whether to ignore public holidays
 * @param {Array<string>} [timeOfUsable.monthsOfYear] - Array of month constants for time
 * restrictions
 * @param {Array<string>} [timeOfUsable.daysOfWeek] - Array of day constants for time restrictions
 * @param {Array<object>} [timeOfUsable.timesOfDay] - Array of time range objects with start/finish
 * properties
 * @param {string} valueField - The field name to extract the numeric value from the timeOfUsable
 * object. Common values include 'price' for Clause objects or 'rate' for VolumeCharge objects
 * @param {boolean} [useInTable=true] - Whether this is used in a table context.
 *   In table contexts, "At all times" is shown for simple clauses to maintain visual consistency.
 *   In list contexts (like cards), simple clauses return an empty string since the value alone
 *   is sufficient.
 * @returns {{value: React.ReactNode[]|null, description: string|null,
 *   conditions: React.ReactElement|null}} Formatted time-of-usable display object containing:
 *   - value: Formatted price/rate with currency symbol and units, or null if invalid input
 *   - description: Human-readable description of time-of-use restrictions
 *     (e.g., "At all times", "January to March, Monday to Friday, between 09:00-17:00")
 *   - conditions: Formatted conditions element containing timezone and holiday settings,
 *     or null if no conditions apply
 */
const useTimeOfUsable = (timeOfUsable, valueField, useInTable = true) => {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const value = timeOfUsable[valueField];

  return {
    value: valuePerUnitWithStrong(intl, intlCurrency, value),
    description: buildDescription(intl, timeOfUsable, useInTable),
    conditions: buildTimeOfUseableElementConditions(intl, timeOfUsable),
  };
};

export default useTimeOfUsable;
